import React, { useState } from "react";

import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { Field, useForm, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

import {
  QUESTION_CHANGE_STATES,
  QUESTION_KEYS,
  QUESTION_OPTION_KEYS,
  QUESTION_TYPES,
} from "schedule-v2/components/recorded-content/recordedContent.data";

import { Button, IconButton, InputAdornment } from "@material-ui/core";
import AddIcon from "@material-ui/icons/AddCircleOutlined";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import DeleteIconLined from "assets/images/delete_lined.svg";

import {
  finalFormParsers,
  required,
  requiredWithMessage,
} from "common/validate";
import { formatters, parsers, validators } from "../../questionDetails.utils";

import { ExlyInput, ExlyTextarea } from "common/form";
import ExlyCheckbox from "common/form/ExlyCheckbox";
import ExlyRadioGroup from "common/form/ExlyRadioGroup/Index";
import ExlySwitch from "common/form/ExlySwitch";
import { Label } from "schedule-v2/components/recorded-content/ExamCreation/components/HelperComponents/HelperComponents";
import { InteractiveTooltip } from "@my-scoot/component-library-legacy";

import { SCORING_TYPES } from "schedule-v2/components/recorded-content/ExamCreation/modals/ScoringType/scoringType.data";
import { FILE_UPLOAD_OPTIONS } from "../../QuestionDetails.data";

import classnames from "classnames";
import styles from "./helperComponents.module.css";
import { InfoOutlined } from "@material-ui/icons";
import { is_empty } from "utils/validations";
import { getAddedOptions } from "../../../../questionsModal.utils";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";

export const QuestionOption = ({
  index,
  name,
  onDelete,
  showDragHandle,
  isDeleted,
  onChange,
}) => {
  if (isDeleted) return null;

  return (
    <Draggable draggableId={name} index={index}>
      {(provided, snapshot) => (
        <div
          className={styles.question}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <Field
            name={name}
            component={ExlyInput}
            placeholder="Enter option..."
            fullWidth
            onChange={onChange}
            endAdornment={
              onDelete && (
                <InputAdornment position="end">
                  <IconButton onClick={onDelete}>
                    <ExlyImage
                      src={DeleteIconLined}
                      alt="delete button icon"
                      className={styles.question__deleteIcon}
                    />
                  </IconButton>
                </InputAdornment>
              )
            }
            wrapperClassName={classnames(
              styles.question__input,
              snapshot.isDragging && styles.question__dragging
            )}
            validate={required}
            validateOnSubmit={true}
          />
          {showDragHandle && (
            <div
              className={styles.question__dragIcon}
              {...provided.dragHandleProps}
            >
              <DragIndicatorIcon />
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

export const AddQuestionCta = ({ index, onClick }) => {
  return (
    <Draggable draggableId={"cta"} index={index}>
      {(provided) => (
        <div ref={provided.innerRef} {...provided.draggableProps}>
          <Button
            className={styles.addCta}
            startIcon={<AddIcon />}
            onClick={onClick}
          >
            Add option
          </Button>
        </div>
      )}
    </Draggable>
  );
};

export const QuestionWrapper = ({ children, isExam, scoringType }) => {
  const isManualScoringType = scoringType === SCORING_TYPES.MANUAL;
  const showTotalMarksField = isExam && isManualScoringType;

  return (
    <div className={styles.questionDetails}>
      {showTotalMarksField && (
        <Field
          name={QUESTION_KEYS.totalMarks}
          component={ExlyInput}
          placeholder="Enter the total marks for this question..."
          fullWidth
          label={
            <div className={styles.questionDetails__label}>
              Total marks<span>*</span>
            </div>
          }
          parse={parsers[QUESTION_KEYS.totalMarks]}
          format={formatters[QUESTION_KEYS.totalMarks]}
          validate={validators[QUESTION_KEYS.totalMarks]}
          validateOnSubmit={true}
          type="text"
        />
      )}

      <Field
        name={QUESTION_KEYS.question}
        component={ExlyTextarea}
        placeholder="Enter question..."
        minHeight="86px"
        minWidth="100%"
        label={
          <div className={styles.questionDetails__label}>
            Question(s)<span>*</span>
          </div>
        }
        validate={requiredWithMessage("Please enter the question.")}
        validateOnSubmit={true}
      />

      {children}

      <div className="d-flex justify-content-between">
        <Label
          label="Is this a mandatory question?"
          tooltip={
            <div>
              <div>
                Mandatory questions are <strong>‘necessary-to-answer’</strong>{" "}
                and the student will be required to answer this question to
                proceed.
              </div>
              <div>Turn the toggle on if you want to make it mandatory.</div>
            </div>
          }
          required={false}
          weight="1"
        />
        <Field
          name={QUESTION_KEYS.isMandatory}
          component={ExlySwitch}
          defaultValue={false}
          color="success"
        />
      </div>
    </div>
  );
};

const handleDragEnd = (fields) => (result) => {
  // dropped outside the list
  if (!result.destination) {
    return;
  }
  if (result.destination.index === -1) return;
  fields.move(result.source.index, result.destination.index);
};

export const SingleOrMultiSelectQuestion = () => {
  const {
    mutators: { push, remove, update },
  } = useForm();
  const {
    values: { [QUESTION_KEYS.options]: options },
  } = useFormState();

  const handleDelete = (optionsKey, index) => {
    const option = options[index] ?? {};
    const { [QUESTION_OPTION_KEYS.optionUid]: optionUid } = option;

    if (is_empty(optionUid)) remove(optionsKey, index);
    else
      update(optionsKey, index, {
        ...option,
        [QUESTION_OPTION_KEYS.changeType]: QUESTION_CHANGE_STATES.DELETED,
      });
  };

  const handleOptionStatementChange = (optionsKey, index, value) => {
    const option = options[index] ?? {};
    const { [QUESTION_OPTION_KEYS.optionUid]: optionUid } = option;

    update(optionsKey, index, {
      ...option,
      [QUESTION_KEYS.changeType]: optionUid
        ? QUESTION_CHANGE_STATES.UPDATED
        : QUESTION_CHANGE_STATES.NEW_ADDED,
      [QUESTION_OPTION_KEYS.option]: value,
    });
  };

  return (
    <div>
      <div className={styles.questionDetails__label}>
        Option(s)<span>*</span>
      </div>

      <FieldArray
        name={QUESTION_KEYS.options}
        validate={validators[QUESTION_KEYS.options]}
      >
        {({ fields }) => (
          <>
            <DragDropContext onDragEnd={handleDragEnd(fields)}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div ref={provided.innerRef}>
                    {fields.map((name, index) => (
                      <QuestionOption
                        key={name}
                        name={`${name}.${QUESTION_OPTION_KEYS.option}`}
                        index={index}
                        onDelete={
                          getAddedOptions(options).length > 1 &&
                          (() => handleDelete(QUESTION_KEYS.options, index))
                        }
                        isDeleted={
                          options[index]?.[QUESTION_OPTION_KEYS.changeType] ==
                          QUESTION_CHANGE_STATES.DELETED
                        }
                        onChange={(value) =>
                          handleOptionStatementChange(
                            QUESTION_KEYS.options,
                            index,
                            value
                          )
                        }
                        option={options[index]}
                        showDragHandle={getAddedOptions(options).length > 1}
                      />
                    ))}
                    <AddQuestionCta
                      index={fields.length}
                      onClick={() =>
                        push(QUESTION_KEYS.options, {
                          [QUESTION_OPTION_KEYS.changeType]:
                            QUESTION_CHANGE_STATES.NEW_ADDED,
                        })
                      }
                    />
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </FieldArray>
    </div>
  );
};

export const NumberScaleRatingQuestion = () => {
  return (
    <Field
      name={QUESTION_KEYS.questionType}
      component={ExlyRadioGroup}
      label="Scale"
      required
      options={[
        { label: "1 - 5", value: QUESTION_TYPES.FIVE_NUMBER_RATING },
        { label: "1 - 10", value: QUESTION_TYPES.TEN_NUMBER_RATING },
      ]}
      isRow
      formControlLabelClassName={styles.radioLabel}
    />
  );
};

export const TextQuestion = () => {
  const {
    values: { [QUESTION_KEYS.metadata]: metadata },
  } = useFormState();
  const { [QUESTION_KEYS.metadataKeys.textLimit]: textLimit } = metadata || {};

  const [enableCharacterLimit, setEnableCharacterLimit] = useState(
    textLimit > 0
  );
  const [openTooltip, setOpenTooltip] = useState(false);

  const handleCloseTooltip = () => setOpenTooltip(false);
  const handleOpenTooltip = () => setOpenTooltip(true);

  return (
    <>
      <div className={styles.characterLimitWrapper}>
        <ExlyCheckbox
          id="characterLimit"
          label={<div>Set character limit</div>}
          onChange={setEnableCharacterLimit}
          input={{
            value: enableCharacterLimit,
            onChange: setEnableCharacterLimit,
          }}
          meta={{}}
          size="large"
        />
        <InteractiveTooltip
          open={openTooltip}
          title="You can set a character limit if you want students to be intentional and precise with their answers."
          primaryBtnText="Got it"
          totalSteps={0}
          tooltipClasses={{
            popper: styles.interactiveToolTipPopper,
            tooltipPlacementBottom: styles.tooltipPlacementBottom,
          }}
          onCloseClick={handleCloseTooltip}
          onPrimaryBtnClick={handleCloseTooltip}
        >
          <InfoOutlined
            className={styles.infoIcon}
            onClick={handleOpenTooltip}
          />
        </InteractiveTooltip>
      </div>

      {enableCharacterLimit && (
        <Field
          name={`${QUESTION_KEYS.metadata}.${QUESTION_KEYS.metadataKeys.textLimit}`}
          component={ExlyInput}
          type="number"
          fullWidth
          placeholder="Enter char limit, for eg: 150"
          format={finalFormParsers.number}
          parse={finalFormParsers.number}
          validate={requiredWithMessage("Please enter character limit.")}
          validateOnSubmit={true}
        />
      )}
    </>
  );
};

export const FileQuestion = () => {
  return (
    <Field
      name={`${QUESTION_KEYS.metadata}.${QUESTION_KEYS.metadataKeys.uploadType}`}
      component={ExlyRadioGroup}
      label="Select file type to be uploaded:"
      options={FILE_UPLOAD_OPTIONS}
      validate={requiredWithMessage("Please select file type.")}
    />
  );
};

export const Question = ({ type, isExam }) => {
  switch (type) {
    case QUESTION_TYPES.SINGLE_SELECT:
    case QUESTION_TYPES.MULTI_SELECT:
      return <SingleOrMultiSelectQuestion />;
    case QUESTION_TYPES.FIVE_NUMBER_RATING:
    case QUESTION_TYPES.TEN_NUMBER_RATING:
      return <NumberScaleRatingQuestion />;
    case QUESTION_TYPES.TEXT_QUESTION:
      return isExam ? <TextQuestion /> : null;
    case QUESTION_TYPES.FILE:
      return isExam ? <FileQuestion /> : null;
    default:
      return null;
  }
};
